import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Provider, useSelector } from 'react-redux';
import './i18n'; // Import i18n configuration

// Store
import { store } from './store/store';

// Components
import Layout from './components/Layout/Layout';
import PrivateRoute from './components/PrivateRoute/PrivateRoute';
import RoleBasedRoute from './components/RoleBasedRoute';
import AuthProvider from './components/AuthProvider/AuthProvider';

// Pages
import Login from './pages/Login';
import Register from './pages/Auth/Register';
import ForgotPassword from './pages/Auth/ForgotPassword';
import ResetPassword from './pages/Auth/ResetPassword';
import Onboarding from './pages/Onboarding/Onboarding';
import Landing from './pages/Landing';
import Dashboard from './pages/Dashboard/Dashboard';
import EmployeePortal from './pages/EmployeePortal';
import Employees from './pages/Employees/Employees';
import EmployeeForm from './pages/EmployeeForm/EmployeeForm';
import Attendance from './pages/Attendance/Attendance';
import LeaveManagement from './pages/Leave/LeaveManagement';
import AdminLeaveManagement from './pages/Leave/AdminLeaveManagement';
import Loan from './pages/Loan/Loan';
import Asset from './pages/Asset/Asset';
import LetterManagement from './pages/Letters/LetterManagement';
import TemplateEditor from './pages/Letters/TemplateEditor';
import HolidayManagement from './pages/HolidayManagement';
import Settings from './pages/Settings/Settings';
import BranchSettings from './pages/Settings/BranchSettings';
import Master from './pages/Master/Master';
import DummyDataDemo from './pages/DummyDataDemo';
import TaxMaster from './pages/Master/TaxMaster';
import SalaryStructure from './pages/Master/SalaryStructure';
import NotificationsPage from './pages/Notifications/NotificationsPage';
import { PayrollManagement } from './pages/Payroll';
import TdsReports from './pages/TdsReports/TdsReports';
import Reports from './pages/Reports/Reports';
import PayrollSummary from './pages/Reports/PayrollSummary';
import PfReport from './pages/Reports/PfReport';
import EsiReport from './pages/Reports/EsiReport';
import PtReport from './pages/Reports/PtReport';
import EmployeeReports from './pages/Reports/EmployeeReports';
import SalarySlipDownload from './pages/Employee/SalarySlipDownload';
import Profile from './pages/Profile/Profile';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import TaskManagement from './pages/Task/TaskManagement';

// Super Admin Pages
import SuperAdminDashboard from './pages/SuperAdmin/SuperAdminDashboard';
import CompanyManagement from './pages/SuperAdmin/CompanyManagement';
import PlanManagement from './pages/SuperAdmin/PlanManagement';
import PaymentSystem from './pages/SuperAdmin/PaymentSystem';
import DataCleanup from './pages/SuperAdmin/DataCleanup';
import InquiryManagement from './pages/SuperAdmin/InquiryManagement';

// Role-based redirect component
const RoleBasedRedirect = () => {
  const { user } = useSelector((state) => state.auth);

  if (user?.role === 'employee') {
    return <Navigate to="/dashboard" replace />;  // ← Direct to dashboard
  }

  return <Navigate to="/dashboard" replace />;
};

// Placeholder page component
function PlaceholderPage({ title }) {
  return (
    <div className="flex items-center justify-center min-h-96">
      <div className="text-center">
        <div className="h-32 w-32 bg-gradient-primary rounded-full flex items-center justify-center mx-auto mb-6">
          <span className="text-white text-4xl font-bold">🚧</span>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">{title}</h1>
        <p className="text-gray-600">This page is under construction</p>
      </div>
    </div>
  );
}

function App() {
  return (
    <Provider store={store}>
      <AuthProvider>
        <div className="App">
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/onboarding" element={<Onboarding />} />

            {/* Protected Routes */}
            <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
              <Route index element={<RoleBasedRedirect />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="analytics" element={
                <RoleBasedRoute allowedRoles={['super_admin', 'company_admin', 'hr_admin']}>
                  <AnalyticsDashboard />
                </RoleBasedRoute>
              } />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="profile" element={<Profile />} />

              {/* Admin-only routes */}
              <Route path="master" element={
                <RoleBasedRoute allowedRoles={['super_admin', 'company_admin', 'hr_admin']}>
                  <Master />
                </RoleBasedRoute>
              } />
              <Route path="tax-master" element={
                <RoleBasedRoute allowedRoles={['super_admin', 'company_admin']}>
                  <TaxMaster />
                </RoleBasedRoute>
              } />
              <Route path="salary-structure" element={
                <RoleBasedRoute allowedRoles={['super_admin', 'company_admin', 'hr_admin']}>
                  <SalaryStructure />
                </RoleBasedRoute>
              } />
              <Route path="employees" element={
                <RoleBasedRoute allowedRoles={['super_admin', 'company_admin', 'hr_admin']}>
                  <Employees />
                </RoleBasedRoute>
              } />
              <Route path="employees/add" element={
                <RoleBasedRoute allowedRoles={['super_admin', 'company_admin', 'hr_admin']}>
                  <EmployeeForm />
                </RoleBasedRoute>
              } />
              <Route path="employees/edit/:id" element={
                <RoleBasedRoute allowedRoles={['super_admin', 'company_admin', 'hr_admin']}>
                  <EmployeeForm />
                </RoleBasedRoute>
              } />
              <Route path="dummy-data" element={
                <RoleBasedRoute allowedRoles={['super_admin', 'company_admin', 'hr_admin']}>
                  <DummyDataDemo />
                </RoleBasedRoute>
              } />
              <Route path="attendance" element={
                <RoleBasedRoute allowedRoles={['super_admin', 'company_admin', 'hr_admin', 'employee']}>
                  <Attendance />
                </RoleBasedRoute>
              } />
      
              <Route path="tasks" element={
                <RoleBasedRoute allowedRoles={['super_admin', 'company_admin', 'hr_admin', 'employee']}>
                  <TaskManagement />
                </RoleBasedRoute>
              } />
              <Route path="performance" element={
                <RoleBasedRoute allowedRoles={['super_admin', 'company_admin', 'hr_admin']}>
                  <PlaceholderPage title="Performance Management" />
                </RoleBasedRoute>
              } />
              <Route path="recruitment" element={
                <RoleBasedRoute allowedRoles={['super_admin', 'company_admin', 'hr_admin']}>
                  <PlaceholderPage title="Recruitment Management" />
                </RoleBasedRoute>
              } />
              <Route path="settings" element={
                <RoleBasedRoute allowedRoles={['super_admin', 'company_admin']}>
                  <Settings />
                </RoleBasedRoute>
              } />
              <Route path="branch-settings" element={
                <RoleBasedRoute allowedRoles={['super_admin', 'company_admin']}>
                  <BranchSettings />
                </RoleBasedRoute>
              } />

              {/* Employee routes (leave management) */}
              <Route path="leave" element={<LeaveManagement />} />
              <Route path="loan" element={<Loan />} />
              <Route path="assets" element={<Asset />} />
              <Route path="letters" element={
                <RoleBasedRoute allowedRoles={['super_admin', 'company_admin', 'hr_admin', 'employee']}>
                  <LetterManagement />
                </RoleBasedRoute>
              } />
              <Route path="letters/create" element={
                <RoleBasedRoute allowedRoles={['super_admin', 'company_admin', 'hr_admin']}>
                  <TemplateEditor />
                </RoleBasedRoute>
              } />
              <Route path="letters/edit/:id" element={
                <RoleBasedRoute allowedRoles={['super_admin', 'company_admin', 'hr_admin']}>
                  <TemplateEditor />
                </RoleBasedRoute>
              } />
              <Route path="salary-slips" element={
                <RoleBasedRoute allowedRoles={['employee']}>
                  <SalarySlipDownload />
                </RoleBasedRoute>
              } />
              <Route path="admin-leave" element={
                <RoleBasedRoute allowedRoles={['super_admin', 'company_admin', 'hr_admin']}>
                  <AdminLeaveManagement />
                </RoleBasedRoute>
              } />
              <Route path="holidays" element={
                <RoleBasedRoute allowedRoles={['super_admin', 'company_admin', 'hr_admin']}>
                  <HolidayManagement />
                </RoleBasedRoute>
              } />
              <Route path="payroll" element={
                <RoleBasedRoute allowedRoles={['super_admin', 'company_admin', 'hr_admin']}>
                  <PayrollManagement />
                </RoleBasedRoute>
              } />
              <Route path="tds-reports" element={
                <RoleBasedRoute allowedRoles={['super_admin', 'company_admin', 'hr_admin']}>
                  <TdsReports />
                </RoleBasedRoute>
              } />
              <Route path="reports" element={
                <RoleBasedRoute allowedRoles={['super_admin', 'company_admin', 'hr_admin']}>
                  <Reports />
                </RoleBasedRoute>
              } />
              <Route path="reports/payroll-summary" element={
                <RoleBasedRoute allowedRoles={['super_admin', 'company_admin', 'hr_admin']}>
                  <PayrollSummary />
                </RoleBasedRoute>
              } />
              <Route path="reports/employee-reports" element={
                <RoleBasedRoute allowedRoles={['super_admin', 'company_admin', 'hr_admin']}>
                  <EmployeeReports />
                </RoleBasedRoute>
              } />
              <Route path="reports/pf" element={
                <RoleBasedRoute allowedRoles={['super_admin', 'company_admin', 'hr_admin']}>
                  <PfReport />
                </RoleBasedRoute>
              } />
              <Route path="reports/esi" element={
                <RoleBasedRoute allowedRoles={['super_admin', 'company_admin', 'hr_admin']}>
                  <EsiReport />
                </RoleBasedRoute>
              } />
              <Route path="reports/pt" element={
                <RoleBasedRoute allowedRoles={['super_admin', 'company_admin', 'hr_admin']}>
                  <PtReport />
                </RoleBasedRoute>
              } />
              <Route path="reports/tds" element={
                <RoleBasedRoute allowedRoles={['super_admin', 'company_admin', 'hr_admin']}>
                  <TdsReports />
                </RoleBasedRoute>
              } />

              {/* Super Admin Only Routes */}
              <Route path="super-admin/dashboard" element={
                <RoleBasedRoute allowedRoles={['super_admin']}>
                  <SuperAdminDashboard />
                </RoleBasedRoute>
              } />
              <Route path="super-admin/companies" element={
                <RoleBasedRoute allowedRoles={['super_admin']}>
                  <CompanyManagement />
                </RoleBasedRoute>
              } />
              <Route path="super-admin/plans" element={
                <RoleBasedRoute allowedRoles={['super_admin']}>
                  <PlanManagement />
                </RoleBasedRoute>
              } />
              <Route path="super-admin/payments" element={
                <RoleBasedRoute allowedRoles={['super_admin']}>
                  <PaymentSystem />
                </RoleBasedRoute>
              } />
              <Route path="super-admin/data-cleanup" element={
                <RoleBasedRoute allowedRoles={['super_admin']}>
                  <DataCleanup />
                </RoleBasedRoute>
              } />
              <Route path="super-admin/inquiries" element={
                <RoleBasedRoute allowedRoles={['super_admin']}>
                  <InquiryManagement />
                </RoleBasedRoute>
              } />
              {/* Redirect /super-admin to /super-admin/dashboard */}
              <Route path="super-admin" element={<Navigate to="/super-admin/dashboard" replace />} />
            </Route>

            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </AuthProvider>
    </Provider>
  );
}

export default App;
